// src/features/clients/ClientsTab.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useClients } from "./hooks/useClients";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "components/ui/table";
import {
  Search,
  Users,
  ArrowRightLeft,
  Edit,
  Trash2,
  Home,
  Bus,
  Layers,
  Calendar,
  Clock,
  MapPin,
  Phone,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  X,
  Sparkles,
  RefreshCw,
  Eye,
  Copy,
  Check,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import ClientsMigrationTool from "./temp/ClientsMigrationTool";
import EditClientModal from "./components/EditClientModal";
import ClientTimelineModal from "./components/ClientTimelineModal";

function ClientsTab() {
  const { clients, isLoading, fetchClients, deleteClient, addClient } =
    useClients();
  const [searchTerm, setSearchTerm] = useState("");
  const [showMigration, setShowMigration] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [timelineClient, setTimelineClient] = useState(null);
  const [copiedPhoneId, setCopiedPhoneId] = useState(null);

  // Estados de Filtros e Ordenação Inteligente
  const [filtroModalidade, setFiltroModalidade] = useState("todos"); // 'todos' | 'chale' | 'excursao' | 'ambos' | 'nenhuma'
  const [filtroRecencia, setFiltroRecencia] = useState("todos"); // 'todos' | 'recentes' | 'moderados' | 'inativos'
  const [ordenacao, setOrdenacao] = useState("recente"); // 'recente' | 'antigo' | 'nome' | 'frequencia'
  const [dataInicioReserva, setDataInicioReserva] = useState("");
  const [dataFimReserva, setDataFimReserva] = useState("");
  const [mostrarFiltroAvancado, setMostrarFiltroAvancado] = useState(false);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Estatísticas Rápidas da Base de Clientes (KPIs)
  const stats = useMemo(() => {
    const total = clients.length;
    const chales = clients.filter(
      (c) => c.modalidade === "chale" || c.modalidade === "ambos"
    ).length;
    const excursoes = clients.filter(
      (c) => c.modalidade === "excursao" || c.modalidade === "ambos"
    ).length;
    const ambos = clients.filter((c) => c.modalidade === "ambos").length;
    const semReserva = clients.filter((c) => c.modalidade === "nenhuma").length;
    const ativosRecentes = clients.filter((c) => c.diasDesdeUltima <= 90).length;
    const inativosReativar = clients.filter(
      (c) => c.diasDesdeUltima > 180 && c.diasDesdeUltima < 999999
    ).length;

    return {
      total,
      chales,
      excursoes,
      ambos,
      semReserva,
      ativosRecentes,
      inativosReativar,
    };
  }, [clients]);

  // Pipeline de Filtragem e Ordenação dos Clientes
  const filteredClients = useMemo(() => {
    let list = [...clients];

    // 1. Busca textual inteligente
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(term)) ||
          (c.phone && c.phone.includes(term)) ||
          (c.city && c.city.toLowerCase().includes(term)) ||
          (c.cpf && c.cpf.includes(term)) ||
          (c.detalheUltima && c.detalheUltima.toLowerCase().includes(term))
      );
    }

    // 2. Filtro de Modalidade
    if (filtroModalidade === "chale") {
      list = list.filter(
        (c) => c.modalidade === "chale" || c.modalidade === "ambos"
      );
    } else if (filtroModalidade === "excursao") {
      list = list.filter(
        (c) => c.modalidade === "excursao" || c.modalidade === "ambos"
      );
    } else if (filtroModalidade === "ambos") {
      list = list.filter((c) => c.modalidade === "ambos");
    } else if (filtroModalidade === "nenhuma") {
      list = list.filter((c) => c.modalidade === "nenhuma");
    }

    // 3. Filtro de Recência
    if (filtroRecencia === "recentes") {
      list = list.filter((c) => c.diasDesdeUltima <= 90);
    } else if (filtroRecencia === "moderados") {
      list = list.filter(
        (c) => c.diasDesdeUltima > 90 && c.diasDesdeUltima <= 180
      );
    } else if (filtroRecencia === "inativos") {
      list = list.filter(
        (c) => c.diasDesdeUltima > 180 && c.diasDesdeUltima < 999999
      );
    }

    // 4. Intervalo de Datas da Última Reserva
    if (dataInicioReserva) {
      const dInicio = new Date(`${dataInicioReserva}T00:00:00`);
      list = list.filter(
        (c) => c.ultimaReservaDate && new Date(c.ultimaReservaDate) >= dInicio
      );
    }
    if (dataFimReserva) {
      const dFim = new Date(`${dataFimReserva}T23:59:59`);
      list = list.filter(
        (c) => c.ultimaReservaDate && new Date(c.ultimaReservaDate) <= dFim
      );
    }

    // 5. Ordenação
    list.sort((a, b) => {
      if (ordenacao === "recente") {
        if (!a.ultimaReservaDate && !b.ultimaReservaDate)
          return (a.name || "").localeCompare(b.name || "");
        if (!a.ultimaReservaDate) return 1;
        if (!b.ultimaReservaDate) return -1;
        return new Date(b.ultimaReservaDate) - new Date(a.ultimaReservaDate);
      }
      if (ordenacao === "antigo") {
        if (!a.ultimaReservaDate && !b.ultimaReservaDate)
          return (a.name || "").localeCompare(b.name || "");
        if (!a.ultimaReservaDate) return 1;
        if (!b.ultimaReservaDate) return -1;
        return new Date(a.ultimaReservaDate) - new Date(b.ultimaReservaDate);
      }
      if (ordenacao === "frequencia") {
        return (b.totalReservas || 0) - (a.totalReservas || 0);
      }
      return (a.name || "").localeCompare(b.name || "");
    });

    return list;
  }, [
    clients,
    searchTerm,
    filtroModalidade,
    filtroRecencia,
    dataInicioReserva,
    dataFimReserva,
    ordenacao,
  ]);

  const limparFiltros = () => {
    setSearchTerm("");
    setFiltroModalidade("todos");
    setFiltroRecencia("todos");
    setOrdenacao("recente");
    setDataInicioReserva("");
    setDataFimReserva("");
  };

  const handleUpdateClient = async (data) => {
    const success = await addClient(data);
    if (success) {
      fetchClients();
      setEditingClient(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return "CL";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const copiarTelefone = (phone, id = null) => {
    if (!phone) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    const raw = String(phone).trim();
    navigator.clipboard.writeText(raw);
    if (id) {
      setCopiedPhoneId(id);
      setTimeout(() => setCopiedPhoneId(null), 2000);
    }
    toast.success(`Telefone ${raw} copiado para o Bot!`, {
      icon: "📋",
      duration: 2500,
    });
  };

  if (showMigration) {
    return (
      <div className="space-y-4">
        <Button
          variant="outline"
          onClick={() => {
            setShowMigration(false);
            fetchClients();
          }}
        >
          ← Voltar para Lista
        </Button>
        <ClientsMigrationTool />
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-6 pb-24 max-w-5xl mx-auto space-y-5">
      {/* Topo do Módulo */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
                Gestão de Clientes
                <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-0.5 rounded-full">
                  {stats.total}
                </span>
              </h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                CRM Inteligente com rastreamento de estadias em chalés e viagens de excursões.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchClients}
            disabled={isLoading}
            className="h-10 text-xs flex-1 sm:flex-none flex items-center justify-center gap-1.5"
            title="Atualizar lista em tempo real"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Sincronizar</span>
          </Button>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowMigration(true)}
            className="h-10 text-xs flex-1 sm:flex-none flex items-center justify-center gap-1.5"
          >
            <ArrowRightLeft className="h-3.5 h-3.5" />
            <span>Migrar Dados</span>
          </Button>
        </div>
      </div>

      {/* KPI Cards Interativos (Tap para filtrar) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {/* Card Chalés */}
        <button
          type="button"
          onClick={() =>
            setFiltroModalidade(filtroModalidade === "chale" ? "todos" : "chale")
          }
          className={`text-left p-3 sm:p-3.5 rounded-2xl border transition-all active:scale-95 shadow-sm relative overflow-hidden ${
            filtroModalidade === "chale"
              ? "bg-emerald-500 text-white border-emerald-600 shadow-md ring-2 ring-emerald-400/40"
              : "bg-card hover:bg-emerald-50/40 dark:hover:bg-emerald-950/20 border-border"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-[11px] font-semibold uppercase tracking-wider ${
                filtroModalidade === "chale" ? "text-emerald-100" : "text-emerald-600 dark:text-emerald-400"
              }`}
            >
              Hóspedes Chalé
            </span>
            <Home
              className={`w-4 h-4 ${
                filtroModalidade === "chale" ? "text-white" : "text-emerald-600 dark:text-emerald-400"
              }`}
            />
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="text-xl sm:text-2xl font-bold">{stats.chales}</span>
            <span
              className={`text-[10px] font-medium ${
                filtroModalidade === "chale" ? "text-emerald-100" : "text-muted-foreground"
              }`}
            >
              clientes
            </span>
          </div>
        </button>

        {/* Card Excursões */}
        <button
          type="button"
          onClick={() =>
            setFiltroModalidade(
              filtroModalidade === "excursao" ? "todos" : "excursao"
            )
          }
          className={`text-left p-3 sm:p-3.5 rounded-2xl border transition-all active:scale-95 shadow-sm relative overflow-hidden ${
            filtroModalidade === "excursao"
              ? "bg-blue-600 text-white border-blue-700 shadow-md ring-2 ring-blue-400/40"
              : "bg-card hover:bg-blue-50/40 dark:hover:bg-blue-950/20 border-border"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-[11px] font-semibold uppercase tracking-wider ${
                filtroModalidade === "excursao" ? "text-blue-100" : "text-blue-600 dark:text-blue-400"
              }`}
            >
              Excursões
            </span>
            <Bus
              className={`w-4 h-4 ${
                filtroModalidade === "excursao" ? "text-white" : "text-blue-600 dark:text-blue-400"
              }`}
            />
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="text-xl sm:text-2xl font-bold">{stats.excursoes}</span>
            <span
              className={`text-[10px] font-medium ${
                filtroModalidade === "excursao" ? "text-blue-100" : "text-muted-foreground"
              }`}
            >
              líderes
            </span>
          </div>
        </button>

        {/* Card Ativos Recentes */}
        <button
          type="button"
          onClick={() =>
            setFiltroRecencia(
              filtroRecencia === "recentes" ? "todos" : "recentes"
            )
          }
          className={`text-left p-3 sm:p-3.5 rounded-2xl border transition-all active:scale-95 shadow-sm relative overflow-hidden ${
            filtroRecencia === "recentes"
              ? "bg-amber-500 text-white border-amber-600 shadow-md ring-2 ring-amber-400/40"
              : "bg-card hover:bg-amber-50/40 dark:hover:bg-amber-950/20 border-border"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-[11px] font-semibold uppercase tracking-wider ${
                filtroRecencia === "recentes" ? "text-amber-100" : "text-amber-600 dark:text-amber-400"
              }`}
            >
              Recentes (&lt; 90d)
            </span>
            <Sparkles
              className={`w-4 h-4 ${
                filtroRecencia === "recentes" ? "text-white" : "text-amber-600 dark:text-amber-400"
              }`}
            />
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="text-xl sm:text-2xl font-bold">
              {stats.ativosRecentes}
            </span>
            <span
              className={`text-[10px] font-medium ${
                filtroRecencia === "recentes" ? "text-amber-100" : "text-muted-foreground"
              }`}
            >
              ativos
            </span>
          </div>
        </button>

        {/* Card Inativos para Reativação */}
        <button
          type="button"
          onClick={() =>
            setFiltroRecencia(
              filtroRecencia === "inativos" ? "todos" : "inativos"
            )
          }
          className={`text-left p-3 sm:p-3.5 rounded-2xl border transition-all active:scale-95 shadow-sm relative overflow-hidden ${
            filtroRecencia === "inativos"
              ? "bg-purple-600 text-white border-purple-700 shadow-md ring-2 ring-purple-400/40"
              : "bg-card hover:bg-purple-50/40 dark:hover:bg-purple-950/20 border-border"
          }`}
        >
          <div className="flex items-center justify-between">
            <span
              className={`text-[11px] font-semibold uppercase tracking-wider ${
                filtroRecencia === "inativos" ? "text-purple-100" : "text-purple-600 dark:text-purple-400"
              }`}
            >
              Reativar (&gt; 6m)
            </span>
            <Clock
              className={`w-4 h-4 ${
                filtroRecencia === "inativos" ? "text-white" : "text-purple-600 dark:text-purple-400"
              }`}
            />
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="text-xl sm:text-2xl font-bold">
              {stats.inativosReativar}
            </span>
            <span
              className={`text-[10px] font-medium ${
                filtroRecencia === "inativos" ? "text-purple-100" : "text-muted-foreground"
              }`}
            >
              oportunidades
            </span>
          </div>
        </button>
      </div>

      {/* Caixa de Busca e Filtros Rápidos */}
      <div className="bg-card border rounded-2xl p-3 sm:p-4 shadow-sm space-y-3">
        {/* Barra de Busca com botão limpar */}
        <div className="relative">
          <Search className="h-4 w-4 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2" />
          <Input
            placeholder="Buscar por nome, telefone, cidade, CPF ou chalé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 h-11 text-xs sm:text-sm rounded-xl"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Scroll Horizontal de Filtros de Modalidade (Pill Chips) */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Modalidade de Reserva:
            </span>
            {(filtroModalidade !== "todos" ||
              filtroRecencia !== "todos" ||
              searchTerm ||
              dataInicioReserva ||
              dataFimReserva) && (
              <button
                type="button"
                onClick={limparFiltros}
                className="text-xs text-primary hover:underline flex items-center gap-1 font-medium"
              >
                <X className="w-3 h-3" /> Limpar Filtros
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            <Button
              variant={filtroModalidade === "todos" ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs rounded-full px-3.5 shrink-0"
              onClick={() => setFiltroModalidade("todos")}
            >
              Todos ({stats.total})
            </Button>
            <Button
              variant={filtroModalidade === "chale" ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs rounded-full px-3.5 shrink-0 flex items-center gap-1.5"
              onClick={() => setFiltroModalidade("chale")}
            >
              <Home className="w-3.5 h-3.5 text-emerald-500" />
              <span>Chalés ({stats.chales})</span>
            </Button>
            <Button
              variant={filtroModalidade === "excursao" ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs rounded-full px-3.5 shrink-0 flex items-center gap-1.5"
              onClick={() => setFiltroModalidade("excursao")}
            >
              <Bus className="w-3.5 h-3.5 text-blue-500" />
              <span>Excursões ({stats.excursoes})</span>
            </Button>
            {stats.ambos > 0 && (
              <Button
                variant={filtroModalidade === "ambos" ? "default" : "outline"}
                size="sm"
                className="h-8 text-xs rounded-full px-3.5 shrink-0 flex items-center gap-1.5"
                onClick={() => setFiltroModalidade("ambos")}
              >
                <Layers className="w-3.5 h-3.5 text-purple-500" />
                <span>Ambos ({stats.ambos})</span>
              </Button>
            )}
            <Button
              variant={filtroModalidade === "nenhuma" ? "default" : "outline"}
              size="sm"
              className="h-8 text-xs rounded-full px-3.5 shrink-0"
              onClick={() => setFiltroModalidade("nenhuma")}
            >
              Sem Reserva ({stats.semReserva})
            </Button>
          </div>
        </div>

        {/* Linha de Ordenação e Botão Avançado */}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t text-xs">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground font-medium shrink-0">Ordenar:</span>
            <Select value={ordenacao} onValueChange={setOrdenacao}>
              <SelectTrigger className="h-8 text-xs font-semibold bg-card dark:bg-[#0c1511] border-border text-foreground rounded-lg px-2.5 focus:ring-1 focus:ring-primary shadow-sm min-w-[190px]">
                <SelectValue placeholder="Selecione a ordem" />
              </SelectTrigger>
              <SelectContent className="bg-card dark:bg-[#0f1c16] text-foreground border-border shadow-xl z-50">
                <SelectItem value="recente" className="text-xs focus:bg-emerald-500/15 focus:text-emerald-400 cursor-pointer py-2">
                  Última Reserva (Mais Recente)
                </SelectItem>
                <SelectItem value="antigo" className="text-xs focus:bg-emerald-500/15 focus:text-emerald-400 cursor-pointer py-2">
                  Última Reserva (Mais Antiga)
                </SelectItem>
                <SelectItem value="frequencia" className="text-xs focus:bg-emerald-500/15 focus:text-emerald-400 cursor-pointer py-2">
                  Mais Reservas (Frequência)
                </SelectItem>
                <SelectItem value="nome" className="text-xs focus:bg-emerald-500/15 focus:text-emerald-400 cursor-pointer py-2">
                  Nome (A - Z)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMostrarFiltroAvancado(!mostrarFiltroAvancado)}
            className="h-8 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <Calendar className="w-3.5 h-3.5" />
            <span>Filtro de Data</span>
            {mostrarFiltroAvancado ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </Button>
        </div>

        {/* Filtro por Intervalo de Data da Última Reserva */}
        {mostrarFiltroAvancado && (
          <div className="pt-2 border-t grid grid-cols-1 sm:grid-cols-2 gap-3 fade-in">
            <div className="space-y-1">
              <Label className="text-xs">Última Reserva a partir de:</Label>
              <Input
                type="date"
                className="h-9 text-xs"
                value={dataInicioReserva}
                onChange={(e) => setDataInicioReserva(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Última Reserva até:</Label>
              <Input
                type="date"
                className="h-9 text-xs"
                value={dataFimReserva}
                onChange={(e) => setDataFimReserva(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Contador de Resultados */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>
          Exibindo <strong>{filteredClients.length}</strong> de{" "}
          <strong>{clients.length}</strong> clientes
        </span>
        {ordenacao === "recente" && (
          <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
            • Ordenado por visita mais recente
          </span>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 📱 VISÃO MOBILE DEDICADA: Cards Touch-Friendly de Alta Densidade */}
      {/* ========================================================================= */}
      <div className="block md:hidden space-y-3">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground space-y-3">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="text-sm font-medium">Cruzando clientes e estadias...</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border rounded-2xl bg-card p-6 space-y-2">
            <Users className="w-10 h-10 mx-auto text-muted-foreground/40" />
            <p className="font-semibold text-base text-foreground">
              Nenhum cliente encontrado
            </p>
            <p className="text-xs text-muted-foreground">
              Tente redefinir os filtros ou buscar por outro termo.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={limparFiltros}
              className="mt-2 text-xs"
            >
              Limpar Filtros
            </Button>
          </div>
        ) : (
          filteredClients.map((client) => {
            const isChale = client.modalidade === "chale";
            const isExcursao = client.modalidade === "excursao";
            const isAmbos = client.modalidade === "ambos";
            const isCopied = copiedPhoneId === client.id;

            return (
              <div
                key={client.id}
                className="bg-card border rounded-2xl p-4 shadow-sm hover:shadow-md transition-all space-y-3 relative overflow-hidden"
              >
                {/* Indicador de Modalidade na Borda Esquerda */}
                <div
                  className={`absolute top-0 left-0 bottom-0 w-1.5 ${
                    isAmbos
                      ? "bg-purple-500"
                      : isChale
                      ? "bg-emerald-500"
                      : isExcursao
                      ? "bg-blue-600"
                      : "bg-slate-300 dark:bg-slate-700"
                  }`}
                />

                {/* Topo do Card: Avatar, Nome, Badges e Botão Copiar Telefone */}
                <div className="flex items-start justify-between gap-2 pl-1">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div
                      className={`w-11 h-11 rounded-xl text-white font-bold text-sm flex items-center justify-center shrink-0 shadow-sm ${
                        isAmbos
                          ? "bg-purple-600"
                          : isChale
                          ? "bg-emerald-600"
                          : isExcursao
                          ? "bg-blue-600"
                          : "bg-slate-500"
                      }`}
                    >
                      {getInitials(client.name)}
                    </div>

                    <div className="min-w-0">
                      <p className="font-bold text-base text-foreground truncate">
                        {client.name}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                        {isAmbos && (
                          <span className="text-[10px] font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Layers className="w-2.5 h-2.5" /> Chalé + Excursão
                          </span>
                        )}
                        {isChale && (
                          <span className="text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Home className="w-2.5 h-2.5" /> Hóspede Chalé
                          </span>
                        )}
                        {isExcursao && (
                          <span className="text-[10px] font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Bus className="w-2.5 h-2.5" /> Excursão
                          </span>
                        )}
                        {client.modalidade === "nenhuma" && (
                          <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                            Sem Reserva
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botão de Copiar Telefone Bruto para o Bot WhatsApp */}
                  {client.phone && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copiarTelefone(client.phone, client.id);
                      }}
                      className={`h-9 px-2.5 rounded-xl flex items-center justify-center gap-1.5 shrink-0 shadow-sm active:scale-95 transition-all text-xs font-semibold border ${
                        isCopied
                          ? "bg-emerald-600 text-white border-emerald-500 ring-2 ring-emerald-400/40"
                          : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/30 dark:bg-emerald-950/40"
                      }`}
                      title="Copiar telefone bruto do banco para o Bot WhatsApp"
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-white" />
                          <span className="text-[11px] font-bold">Copiado!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span className="text-[11px]">Copiar</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Informações de Contato e Cidade */}
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pl-1">
                  <button
                    type="button"
                    onClick={() => copiarTelefone(client.phone, client.id)}
                    className="flex items-center gap-1.5 truncate text-left hover:text-emerald-400 transition-colors group cursor-pointer"
                    title="Clique para copiar telefone para o Bot"
                  >
                    <Phone className="w-3.5 h-3.5 text-muted-foreground group-hover:text-emerald-400 shrink-0" />
                    <span className="truncate group-hover:underline">{client.phone || "Sem telefone"}</span>
                  </button>
                  <div className="flex items-center gap-1.5 truncate">
                    <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <span className="truncate">{client.city || "Cidade N/I"}</span>
                  </div>
                </div>

                {/* Box de Inteligência de Última Reserva */}
                <div className="bg-muted/40 rounded-xl p-3 border border-border/50 space-y-1.5 pl-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-primary" />
                      Última Reserva:
                    </span>
                    {client.ultimaReservaFormatada ? (
                      <span className="font-bold text-foreground">
                        {client.ultimaReservaFormatada}
                      </span>
                    ) : (
                      <span className="text-muted-foreground italic">
                        Sem registro
                      </span>
                    )}
                  </div>

                  {client.ultimaReservaFormatada && (
                    <div className="flex items-center justify-between text-[11px] pt-1 border-t border-border/30">
                      <span className="text-muted-foreground truncate max-w-[180px]">
                        {client.detalheUltima}
                      </span>
                      <span
                        className={`font-semibold px-2 py-0.5 rounded-full ${
                          client.diasDesdeUltima <= 30
                            ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
                            : client.diasDesdeUltima <= 90
                            ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
                            : "bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300"
                        }`}
                      >
                        {client.rotuloRecencia}
                      </span>
                    </div>
                  )}

                  {client.totalReservas > 0 && (
                    <div className="text-[11px] text-muted-foreground flex items-center justify-between pt-0.5">
                      <span>Histórico total:</span>
                      <strong className="text-foreground">
                        {client.totalReservas}{" "}
                        {client.totalReservas === 1 ? "reserva" : "reservas"} (
                        {client.totalChales}{" "}
                        {client.totalChales === 1 ? "chalé" : "chalés"} •{" "}
                        {client.totalExcursoes}{" "}
                        {client.totalExcursoes === 1 ? "excursão" : "excursões"}
                        )
                      </strong>
                    </div>
                  )}
                </div>

                {/* Botões de Ação Touch */}
                <div className="grid grid-cols-3 gap-2 pt-1 pl-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 text-xs flex items-center justify-center gap-1 text-primary border-primary/30 hover:bg-primary/5 active:scale-95 transition-transform"
                    onClick={() => setTimelineClient(client)}
                  >
                    <Eye className="w-4 h-4" />
                    <span>Histórico</span>
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-10 text-xs flex items-center justify-center gap-1 active:scale-95 transition-transform"
                    onClick={() => setEditingClient(client)}
                  >
                    <Edit className="w-4 h-4 text-muted-foreground" />
                    <span>Editar</span>
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 text-xs text-destructive hover:bg-red-50 dark:hover:bg-red-950/40 flex items-center justify-center gap-1 active:scale-95 transition-transform"
                    onClick={() => deleteClient(client.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Excluir</span>
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ========================================================================= */}
      {/* 💻 VISÃO DESKTOP: Tabela Corporativa Refinada */}
      {/* ========================================================================= */}
      <div className="hidden md:block rounded-2xl border bg-card shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Cliente</TableHead>
              <TableHead>Contato</TableHead>
              <TableHead>Modalidade</TableHead>
              <TableHead>Última Reserva</TableHead>
              <TableHead className="text-center">Total Reservas</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-primary mb-2" />
                  <span className="text-sm text-muted-foreground">
                    Carregando clientes e calculando estadias...
                  </span>
                </TableCell>
              </TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  Nenhum cliente encontrado com os filtros aplicados.
                </TableCell>
              </TableRow>
            ) : (
              filteredClients.map((client) => {
                const isCopied = copiedPhoneId === client.id;
                return (
                  <TableRow
                    key={client.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    {/* Coluna Cliente */}
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary font-bold text-xs flex items-center justify-center shrink-0">
                          {getInitials(client.name)}
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-foreground">
                            {client.name}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            {client.city ? (
                              <>
                                <MapPin className="w-3 h-3 text-muted-foreground" />
                                {client.city}
                              </>
                            ) : (
                              "Cidade não informada"
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>

                    {/* Coluna Contato */}
                    <TableCell>
                      <div className="text-xs font-mono flex items-center gap-1.5">
                        <span>{client.phone || "Sem telefone"}</span>
                        {client.phone && (
                          <button
                            type="button"
                            onClick={() => copiarTelefone(client.phone, client.id)}
                            className={`p-1 rounded transition-colors ${
                              isCopied
                                ? "text-emerald-400 bg-emerald-500/20"
                                : "text-muted-foreground hover:text-emerald-400 hover:bg-muted"
                            }`}
                            title="Copiar telefone para o Bot WhatsApp"
                          >
                            {isCopied ? (
                              <Check className="w-3.5 h-3.5" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </div>
                      {client.cpf && (
                        <div className="text-[11px] text-muted-foreground font-mono">
                          CPF: {client.cpf}
                        </div>
                      )}
                    </TableCell>

                    {/* Coluna Modalidade */}
                    <TableCell>
                      {client.modalidade === "ambos" && (
                        <span className="text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <Layers className="w-3 h-3" /> Chalé + Excursão
                        </span>
                      )}
                      {client.modalidade === "chale" && (
                        <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <Home className="w-3 h-3" /> Hóspede Chalé
                        </span>
                      )}
                      {client.modalidade === "excursao" && (
                        <span className="text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-300 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                          <Bus className="w-3 h-3" /> Excursão
                        </span>
                      )}
                      {client.modalidade === "nenhuma" && (
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          Sem Reserva
                        </span>
                      )}
                    </TableCell>

                    {/* Coluna Última Reserva */}
                    <TableCell>
                      {client.ultimaReservaFormatada ? (
                        <div>
                          <div className="font-semibold text-xs text-foreground flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-primary" />
                            {client.ultimaReservaFormatada}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {client.detalheUltima} •{" "}
                            <span className="font-medium text-primary">
                              {client.rotuloRecencia}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          Nenhum registro
                        </span>
                      )}
                    </TableCell>

                    {/* Coluna Total de Reservas */}
                    <TableCell className="text-center">
                      <div className="font-bold text-sm text-foreground">
                        {client.totalReservas || 0}
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {client.totalChales} chalé • {client.totalExcursoes} exc.
                      </div>
                    </TableCell>

                    {/* Coluna Ações */}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs text-primary"
                          onClick={() => setTimelineClient(client)}
                          title="Ver linha do tempo de estadias"
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Histórico
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => setEditingClient(client)}
                          title="Editar dados cadastrais"
                        >
                          <Edit className="h-3.5 h-3.5 text-muted-foreground" />
                        </Button>

                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => deleteClient(client.id)}
                          title="Excluir cliente"
                        >
                          <Trash2 className="h-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Modal de Histórico e Linha do Tempo */}
      {timelineClient && (
        <ClientTimelineModal
          isOpen={!!timelineClient}
          onClose={() => setTimelineClient(null)}
          client={timelineClient}
          onEdit={(c) => setEditingClient(c)}
        />
      )}

      {/* Modal de Edição de Cliente */}
      {editingClient && (
        <EditClientModal
          isOpen={!!editingClient}
          onClose={() => setEditingClient(null)}
          onSave={handleUpdateClient}
          clientData={editingClient}
        />
      )}
    </div>
  );
}

export default ClientsTab;
